import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBilling } from "../../contexts/BillingContext";
import BrandLogo from "../common/BrandLogo";
import { getStandbyPromptSessionKey, shouldOpenStandbyPrompt, shouldResetStandbyPrompt } from "../../utils/standbyPrompt";
import {
  IconChartColumn,
  IconDatabase,
  IconHouse,
  IconLock,
  IconLogOut,
  IconMenu,
  IconPiggyBank,
  IconX,
} from "../common/WireframeIcons";

export type DashboardTab = "operations" | "insights" | "savings" | "cards" | "settings" | "annual" | "account" | "admin";

interface DashboardLayoutProps {
  active: DashboardTab;
  children: React.ReactNode;
}

type NavigationIcon = "settings" | "operations" | "insights" | "savings" | "account" | "admin" | "logout" | "menu" | "close";

function NavGlyph({ type, className = "w-5 h-5" }: { type: NavigationIcon; className?: string }) {
  if (type === "settings") return <IconDatabase className={className} />;
  if (type === "operations") return <IconHouse className={className} />;
  if (type === "insights") return <IconChartColumn className={className} />;
  if (type === "savings") return <IconPiggyBank className={className} />;
  if (type === "account") return <IconLock className={className} />;
  if (type === "admin") return <IconChartColumn className={className} />;
  if (type === "logout") return <IconLogOut className={className} />;
  if (type === "menu") return <IconMenu className={className} />;
  return <IconX className={className} />;
}

export function DashboardLayout({ active, children }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const { snapshot, startCheckout } = useBilling();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [billingActionLoading, setBillingActionLoading] = React.useState<null | "month" | "year">(null);
  const [standbyModalOpen, setStandbyModalOpen] = React.useState(false);

  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navigation = [
    {
      key: "settings" as const,
      label: "Configuración inicial",
      description: "Configura cuentas y carga movimientos",
      path: "/dashboard/configuracion",
      step: 1,
      icon: "settings" as const,
    },
    {
      key: "operations" as const,
      label: "Categorizar movimientos",
      description: "Asigna categorías a tus transacciones",
      path: "/dashboard/movimientos",
      step: 2,
      icon: "operations" as const,
    },
    {
      key: "insights" as const,
      label: "Análisis y resultados",
      description: "Visualiza tus finanzas en gráficos",
      path: "/dashboard/analisis",
      step: 3,
      icon: "insights" as const,
    },
    {
      key: "savings" as const,
      label: "Plan de Ahorro",
      description: "Define y sigue tus metas de ahorro",
      path: "/dashboard/plan-ahorro",
      step: 4,
      icon: "savings" as const,
    },
  ];
  const adminNavigation = user?.isAdmin
    ? [
        {
          key: "admin" as const,
          label: "Métricas admin",
          description: "Uso, planes y conversión",
          path: "/dashboard/admin-metrics",
          step: "A",
          icon: "admin" as const,
        },
      ]
    : [];
  const combinedNavigation = [...navigation, ...adminNavigation];
  const isItemActive = (path: string) => {
    if (path === "/dashboard/analisis") return location.pathname.startsWith("/dashboard/analisis");
    if (path === "/dashboard/movimientos") return location.pathname === "/dashboard/movimientos";
    return location.pathname === path;
  };

  const movementUsage = snapshot?.usage?.movements_processed_month ?? 0;
  const movementLimit = snapshot?.plan?.limits?.movementsProcessedMonthMax;
  const movementLimitNumber = typeof movementLimit === "number" ? movementLimit : null;
  const isTrialing = Boolean(snapshot?.subscription?.status === "trialing");
  const hasStripeSubscription = Boolean(snapshot?.subscription?.provider === "stripe" && !snapshot?.isBypassed);
  const hasCancelScheduled = Boolean(hasStripeSubscription && snapshot?.subscription?.cancelAtPeriodEnd);
  const hasStripeTrialingSubscription = Boolean(
    snapshot?.subscription?.status === "trialing" && snapshot?.subscription?.provider === "stripe" && !snapshot?.isBypassed
  );
  const trialDaysLeft = React.useMemo(() => {
    if (!isTrialing || !snapshot?.subscription?.periodEnd) return null;
    const endTime = new Date(snapshot.subscription.periodEnd).getTime();
    if (!Number.isFinite(endTime)) return null;
    const msLeft = endTime - Date.now();
    if (msLeft <= 0) return 0;
    return Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  }, [isTrialing, snapshot?.subscription?.periodEnd]);
  const billingPlanLabel = snapshot?.isBypassed
    ? "Admin/QA"
    : hasCancelScheduled && isTrialing
      ? "Prueba activa (baja programada)"
      : hasCancelScheduled
        ? "Pro activo (baja programada)"
        : hasStripeTrialingSubscription
          ? "Pro activo"
          : isTrialing
            ? `Prueba gratis (${trialDaysLeft ?? 30} días)`
            : snapshot?.plan?.name ?? "Sin plan";
  const hasStandbyOverLimit = Boolean(snapshot && !snapshot.isBypassed && snapshot.standby?.hasOverLimit);
  const standbyPromptSessionKey = React.useMemo(() => getStandbyPromptSessionKey(user?.id), [user?.id]);

  const handleCheckout = async (interval: "month" | "year") => {
    setBillingActionLoading(interval);
    try {
      await startCheckout(interval);
    } finally {
      setBillingActionLoading(null);
    }
  };

  const isFreePlan = Boolean(snapshot && !snapshot.isBypassed && snapshot.plan.code === "free");

  React.useEffect(() => {
    const isBypassed = Boolean(snapshot?.isBypassed);
    if (!snapshot || shouldResetStandbyPrompt({ hasStandbyOverLimit, isBypassed })) {
      setStandbyModalOpen(false);
      if (typeof window !== "undefined") {
        sessionStorage.removeItem(standbyPromptSessionKey);
      }
      return;
    }
    if (typeof window === "undefined") return;
    const alreadyShownInSession = sessionStorage.getItem(standbyPromptSessionKey) === "1";
    if (!shouldOpenStandbyPrompt({ hasStandbyOverLimit, isBypassed, alreadyShownInSession })) return;
    sessionStorage.setItem(standbyPromptSessionKey, "1");
    setStandbyModalOpen(true);
  }, [hasStandbyOverLimit, snapshot, standbyPromptSessionKey]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="lg:hidden bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="brand">
            <BrandLogo size={28} />
          </Link>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(open => !open)}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
          >
            {mobileMenuOpen ? <NavGlyph type="close" className="w-6 h-6" /> : <NavGlyph type="menu" className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <nav className="border-t border-gray-200 bg-white">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Flujo de trabajo</p>
            </div>
            {combinedNavigation.map(item => {
              const activePath = isItemActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 ${
                    activePath ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex-shrink-0 flex items-center gap-2 pt-0.5">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        activePath ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {item.step}
                    </div>
                    <span className={activePath ? "text-blue-600" : "text-gray-700"}>
                      <NavGlyph type={item.icon} />
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium text-sm ${activePath ? "text-blue-600" : "text-gray-900"}`}>{item.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                  </div>
                </Link>
              );
            })}
            {snapshot && (
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{billingPlanLabel}</p>
                {movementLimitNumber !== null && (
                  <p className="text-xs text-gray-600 mt-1">
                    Movimientos: {movementUsage}/{movementLimitNumber}
                  </p>
                )}
                {isFreePlan ? (
                  <div className="mt-3 grid grid-cols-1 gap-2">
                    <button
                      type="button"
                      className="w-full rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-left text-xs font-semibold text-amber-800"
                      onClick={() => void handleCheckout("month")}
                      disabled={billingActionLoading !== null}
                    >
                      {billingActionLoading === "month" ? "Abriendo checkout..." : "Pro mensual · 2,99 €/mes"}
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-left text-xs font-semibold text-sky-800"
                      onClick={() => void handleCheckout("year")}
                      disabled={billingActionLoading !== null}
                    >
                      {billingActionLoading === "year" ? "Abriendo checkout..." : "Pro anual · 29 €/año"}
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/dashboard/mi-cuenta"
                    className="mt-3 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700"
                  >
                    <span>Mi cuenta y facturación</span>
                    <NavGlyph type="account" className="w-4 h-4" />
                  </Link>
                )}
              </div>
            )}
            <button
              onClick={logout}
              className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 w-full"
            >
              <NavGlyph type="logout" className="w-5 h-5" />
              <span>Cerrar Sesión</span>
            </button>
          </nav>
        )}
      </header>

      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-200 overflow-y-auto z-30">
        <div className="p-6">
          <Link to="/" className="brand">
            <BrandLogo size={32} withTagline />
          </Link>
          <p className="text-sm text-gray-500 mt-3">Finanzas familiares</p>
        </div>

        <div className="px-4 pb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Flujo de trabajo</p>
        </div>

        <nav className="px-3 pb-64">
          {combinedNavigation.map((item, index) => {
            const activePath = isItemActive(item.path);
            return (
              <div key={item.path} className="relative">
                <Link
                  to={item.path}
                  className={`flex items-start gap-3 px-3 py-3 rounded-lg mb-2 transition-colors ${
                    activePath ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        activePath ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {item.step}
                    </div>
                    <span className={activePath ? "text-blue-600" : "text-gray-700"}>
                      <NavGlyph type={item.icon} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm ${activePath ? "text-blue-600" : "text-gray-900"}`}>{item.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                  </div>
                </Link>
                {index < combinedNavigation.length - 1 && <div className="absolute left-6 top-full w-0.5 h-2 bg-gray-200" />}
              </div>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
          {snapshot && (
            <div className="mb-3">
              {isFreePlan ? (
                <div className="w-full rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-left">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-amber-800">Plan Free</p>
                  <p className="text-xs font-medium text-amber-900 mt-1">
                    {movementLimitNumber !== null ? `Movimientos ${movementUsage}/${movementLimitNumber}` : "Límites activos"}
                  </p>
                  {hasStandbyOverLimit ? (
                    <p className="text-xs font-medium text-amber-900 mt-1">
                      {`${snapshot?.standby?.accounts.standby ?? 0} cuentas y ${snapshot?.standby?.cards.standby ?? 0} tarjetas en standby`}
                    </p>
                  ) : null}
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <button
                      type="button"
                      className="w-full rounded-md border border-amber-400 bg-white/90 px-2.5 py-1.5 text-xs font-semibold text-amber-900 hover:bg-white disabled:opacity-70"
                      onClick={() => void handleCheckout("month")}
                      disabled={billingActionLoading !== null}
                    >
                      {billingActionLoading === "month" ? "Abriendo checkout..." : "Pro mensual · 2,99 €/mes"}
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-md border border-sky-300 bg-sky-50 px-2.5 py-1.5 text-xs font-semibold text-sky-900 hover:bg-sky-100 disabled:opacity-70"
                      onClick={() => void handleCheckout("year")}
                      disabled={billingActionLoading !== null}
                    >
                      {billingActionLoading === "year" ? "Abriendo checkout..." : "Pro anual · 29 €/año"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="sidebar-billing-pro">
                  <p className="sidebar-billing-pro-title">
                    {hasCancelScheduled
                      ? "Baja programada"
                      : hasStripeSubscription
                        ? "Suscripción Pro activa"
                        : isTrialing
                          ? "Prueba activa"
                          : "Suscripción activa"}
                  </p>
                  <p className="sidebar-billing-pro-text">
                    {snapshot.isBypassed ? "Usuario interno" : `Plan ${billingPlanLabel}`}
                  </p>
                  <div className="sidebar-billing-pro-actions">
                    <Link
                      to="/dashboard/mi-cuenta"
                      className="sidebar-billing-pro-button"
                    >
                      Mi cuenta
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium text-gray-900">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg w-full"
          >
            <NavGlyph type="logout" className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen">
        <div className="pb-20 lg:pb-8">
          {hasStandbyOverLimit && (
            <div className="px-4 pt-4 lg:px-8">
              <div className="w-full rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-left">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Elementos en standby</p>
                <p className="text-sm text-amber-900 mt-1">
                  {snapshot?.standby?.message ??
                    "Has superado los límites del plan Free. Pásate a Pro para reactivar todos tus datos."}
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void handleCheckout("month")}
                    disabled={billingActionLoading !== null}
                  >
                    {billingActionLoading === "month" ? "Abriendo checkout..." : "Pro mensual · 2,99 €/mes"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => void handleCheckout("year")}
                    disabled={billingActionLoading !== null}
                  >
                    {billingActionLoading === "year" ? "Abriendo checkout..." : "Pro anual · 29 €/año"}
                  </button>
                </div>
              </div>
            </div>
          )}
          {children}
        </div>
      </main>

      {standbyModalOpen && hasStandbyOverLimit && (
        <div className="settings-drawer-container settings-drawer-container--modal" role="dialog" aria-modal="true" aria-labelledby="standby-plan-title">
          <button
            type="button"
            className="settings-drawer-backdrop"
            onClick={() => setStandbyModalOpen(false)}
            aria-label="Cerrar modal"
          />
          <div className="settings-drawer settings-drawer--modal billing-limit-modal">
            <button
              type="button"
              className="settings-drawer-close"
              onClick={() => setStandbyModalOpen(false)}
              aria-label="Cerrar"
            >
              <NavGlyph type="close" className="w-5 h-5" />
            </button>
            <p className="settings-drawer-label">Plan Free</p>
            <h3 id="standby-plan-title" className="settings-drawer-title">Tienes elementos en standby</h3>
            <p className="settings-drawer-subtitle standby-upgrade-copy standby-upgrade-copy--desktop">
              {snapshot?.standby?.message ??
                "Has superado los límites del plan Free. Pásate a Pro para reactivar todos tus datos."}
            </p>
            <p className="settings-drawer-subtitle standby-upgrade-copy standby-upgrade-copy--mobile">
              Has superado los límites del plan Free. Pásate a Pro para reactivar tus datos.
            </p>
            <div className="panel" style={{ background: "#f8fafc", borderColor: "#dbe4f0" }}>
              <p style={{ margin: 0, color: "#2c3e50", fontSize: "0.9rem" }}>
                {`${snapshot?.standby?.accounts.standby ?? 0} cuentas y ${snapshot?.standby?.cards.standby ?? 0} tarjetas están fuera de cómputo en análisis y totalizaciones.`}
              </p>
            </div>
            <div className="settings-drawer-actions standby-upgrade-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void handleCheckout("month")}
                disabled={billingActionLoading !== null}
              >
                {billingActionLoading === "month" ? "Abriendo checkout..." : "Pro mensual · 2,99 €/mes"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void handleCheckout("year")}
                disabled={billingActionLoading !== null}
              >
                {billingActionLoading === "year" ? "Abriendo checkout..." : "Pro anual · 29 €/año"}
              </button>
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setStandbyModalOpen(false)}
                disabled={billingActionLoading !== null}
              >
                Más tarde
              </button>
            </div>
          </div>
        </div>
      )}

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-inset-bottom z-40">
        <div className="grid grid-cols-4 gap-1 px-2 py-2">
          {[
            { key: "settings", path: "/dashboard/configuracion", icon: "settings" as const, label: "Configurar" },
            { key: "operations", path: "/dashboard/movimientos", icon: "operations" as const, label: "Movimientos" },
            { key: "insights", path: "/dashboard/analisis", icon: "insights" as const, label: "Análisis" },
            user?.isAdmin
              ? { key: "admin", path: "/dashboard/admin-metrics", icon: "admin" as const, label: "Admin" }
              : { key: "savings", path: "/dashboard/plan-ahorro", icon: "savings" as const, label: "Ahorro" },
          ].map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 py-2 rounded-lg ${
                active === (item.key as DashboardTab) ? "text-blue-600" : "text-gray-600"
              }`}
            >
              <NavGlyph type={item.icon} className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
